import { BarChart, ChartsReferenceLine, ChartsTooltipContainer, LineChart } from '@mui/x-charts';
import { useItemTooltip, useAxesTooltip } from '@mui/x-charts/ChartsTooltip';
import { toDate } from 'date-fns-tz';
import moment from 'moment-timezone';
import React, { useMemo } from 'react';
// Replace Google Charts with MUI X Charts
import { PersonVitalInsights, PersonVitalType, PersonVitalTimeFrame } from '@redux/person/personTypes';
import { CircledCustomIcon, TableIcon } from 'components/Icons';
import { Text } from 'components/Typography';
import { ChartProps } from 'containers/PatientDashboard/components/Charts/Charts';
import { createCustomPointTooltip, getMinTime, getChartColor, getDateFormat } from 'utils/charts';
import { getDateFromStringIgnoreTimezone } from 'utils/time';
import { getFixedPointDisplay } from 'utils/util';
import style from './InsightsLineChart.scss';

type InsightsLineChartProps = Omit<ChartProps, 'isLoadingData'>;

const CHART_HEIGHT = 466;

export const InsightsLineChart: React.FC<InsightsLineChartProps> = ({
  timeZoneId,
  personVitalInsights,
  vitalType,
  timeFrame,
  selectedDate,
  minRate,
  maxRate,
  minControlRate,
  maxControlRate,
  onDaySelect,
  onChartDataAvailabilityChange
}) => {
  // Determine chart time range in patient's timezone
  const patTzMoment = moment.tz(selectedDate, timeZoneId);
  const isToday = patTzMoment.isSame(moment().tz(timeZoneId), 'day');
  let xMinMoment: moment.Moment;
  let xMaxMoment: moment.Moment;
  if (timeFrame === PersonVitalTimeFrame.TWENTY_FOUR_HOURS) {
    if (isToday) {
      xMaxMoment = moment().tz(timeZoneId);
    } else {
      xMaxMoment = patTzMoment.clone().startOf('day').add(1, 'day').hour(17).minute(0).second(0);
    }
    xMinMoment = xMaxMoment.clone().subtract(1, 'day');
  } else {
    xMaxMoment = patTzMoment.clone().startOf('day').add(1, 'day');
    xMinMoment = moment.tz(getMinTime(xMaxMoment.toDate(), timeFrame), timeZoneId);
  }
  const xMinValue = xMinMoment.toDate();
  const xMaxValue = xMaxMoment.toDate();
  const chartColor = getChartColor(vitalType);
  const title = '';

  const nominalBounds =
    vitalType === PersonVitalType.BED_EXITS
      ? [0, 4]
      : vitalType === PersonVitalType.NIGHT_SLEEP_TIME
        ? [4.22 * 60, 10.2 * 60]
        : vitalType === PersonVitalType.NIGHT_AWAKE_TIME
          ? [0, 103]
          : vitalType === PersonVitalType.DAY_SLEEP_TIME
            ? [0, 45]
            : [0, 0];

  const isDateValid = (dt: Date | null) => {
    return Boolean(dt && dt >= xMinValue && dt <= xMaxValue);
  };

  const isValueValid = (value: number | undefined) => {
    return typeof value === 'number' && !Number.isNaN(value) && value >= 0;
  };

  interface DataPoint {
    timestamp: Date;
    value: number;
    tooltipHtml: string;
    [key: string]: string | number | Date | null | undefined;
  }

  const formatValueString = (value: number) => (vitalType == PersonVitalType.BED_EXITS ? `${value}` : `${Math.floor(value / 60)}h ${String(value % 60).padStart(2, '0')}m`);

  // MUI BarChart only renders x-axis labels for data within the chart, regardless of x-axis min/max
  // So we need to generate the x-axis labels manually by pushing the timestamps into the chartDataObjects
  const chartDataObjects: DataPoint[] = useMemo(() => {
    const aggregated = personVitalInsights?.listOfAggregatedSleepMetrics ?? [];
    const rawMap = new Map<number, { rawVal: number; status?: any }>();
    aggregated.forEach((vitalSign) => {
      let rawVal = 0;
      switch (vitalType) {
        case PersonVitalType.NIGHT_AWAKE_TIME:
          rawVal = vitalSign.nightAwakeTimeInMinutes;
          break;
        case PersonVitalType.NIGHT_SLEEP_TIME:
          rawVal = vitalSign.nightSleepTimeInMinutes;
          break;
        case PersonVitalType.BED_EXITS:
          rawVal = vitalSign.numberOfBedExits;
          break;
        case PersonVitalType.DAY_SLEEP_TIME:
          rawVal = vitalSign.daySleepTimeInMinutes;
          break;
      }
      // vitalSign.bucket is already in patient's timezone but because of the way BE structures the data, it is returned as if it were in UTC
      // Adjust the time to keep the hours/minutes but interpret it as the patient's timezone
      const timeZoneDateTimeString = vitalSign.bucketLocal?.endsWith('Z') ? vitalSign.bucketLocal?.substring(0, vitalSign.bucketLocal?.length - 1) : vitalSign.bucketLocal;
      let start = toDate(timeZoneDateTimeString, { timeZone: timeZoneId });
      if (!start) return;
      let end = moment(start).add(30, 'minutes').toDate();
      if (timeFrame === PersonVitalTimeFrame.TWELVE_MONTHS) {
        start = moment.tz(start, timeZoneId).startOf('month').toDate();
        end = moment.tz(start, timeZoneId).add(30, 'minutes').toDate();
      } else if (timeFrame === PersonVitalTimeFrame.SEVEN_DAYS || timeFrame === PersonVitalTimeFrame.THIRTY_DAYS) {
        // For 7-day and 30-day timeframes, bucket is a day, so adjust start and end to cover the whole day
        start = moment.tz(start, timeZoneId).startOf('day').toDate();
        end = moment.tz(start, timeZoneId).add(30, 'minutes').toDate();
      }
      if (!isValueValid(rawVal) || !isDateValid(start) || !isDateValid(end)) return;
      if (timeFrame === PersonVitalTimeFrame.TWENTY_FOUR_HOURS) {
        rawMap.set(moment.tz(start, timeZoneId).valueOf(), { rawVal });
      } else {
        rawMap.set(moment.tz(start, timeZoneId).startOf('day').valueOf(), {
          rawVal
        });
      }
    });
    // if no real data, do not render empty chart, trigger noData view
    if (rawMap.size === 0) {
      return [];
    }
    const steps: Date[] = [];
    // Generate x-axis steps based on patient's timezone
    const startMoment = moment.tz(xMinValue, timeZoneId);
    const endMoment = moment.tz(xMaxValue, timeZoneId);
    switch (timeFrame) {
      case PersonVitalTimeFrame.TWENTY_FOUR_HOURS: {
        const stepMinutes = 30;
        let bucketStart = startMoment.clone();
        const remainder = bucketStart.minute() % stepMinutes;
        if (remainder !== 0) bucketStart = bucketStart.add(stepMinutes - remainder, 'minutes');
        bucketStart = bucketStart.seconds(0).milliseconds(0);
        let current = bucketStart.clone();
        while (current <= endMoment) {
          steps.push(current.toDate());
          current = current.add(stepMinutes, 'minutes');
        }
        break;
      }
      case PersonVitalTimeFrame.SEVEN_DAYS:
      case PersonVitalTimeFrame.THIRTY_DAYS: {
        const daysCount = timeFrame === PersonVitalTimeFrame.SEVEN_DAYS ? 7 : 30;
        const dayStart = patTzMoment
          .clone()
          .startOf('day')
          .subtract(daysCount - 1, 'days');
        let current = dayStart.clone();
        for (let i = 0; i < daysCount; i++) {
          steps.push(current.toDate());
          current = current.add(1, 'day');
        }
        break;
      }
      case PersonVitalTimeFrame.TWELVE_MONTHS: {
        const monthsCount = 12;
        const monthStart = moment
          .tz(xMaxValue, timeZoneId)
          .startOf('month')
          .subtract(monthsCount - 1, 'months');
        let current = monthStart.clone();
        for (let i = 0; i < monthsCount; i++) {
          steps.push(current.toDate());
          current = current.add(1, 'month');
        }
        break;
      }
      default:
        break;
    }
    return steps.map((timestamp) => {
      const bucket = rawMap.get(timestamp.getTime());
      let value: number;
      let status: any;
      if (bucket) {
        value = bucket.rawVal;
        status = bucket.status;
      } else {
        value = 0;
        status = undefined;
      }
      let end: Date;
      switch (timeFrame) {
        case PersonVitalTimeFrame.TWENTY_FOUR_HOURS:
          end = moment(timestamp).add(30, 'minutes').toDate();
          break;
        case PersonVitalTimeFrame.SEVEN_DAYS:
        case PersonVitalTimeFrame.THIRTY_DAYS:
          end = moment(timestamp).add(1, 'day').toDate();
          break;
        case PersonVitalTimeFrame.TWELVE_MONTHS:
          end = moment(timestamp).add(1, 'month').toDate();
          break;
        default:
          end = moment(timestamp).add(30, 'minutes').toDate();
      }
      const tooltipHtml = createCustomPointTooltip(
        vitalType,
        timestamp,
        end,
        formatValueString(value),
        vitalType == PersonVitalType.BED_EXITS ? 'exit(s)' : '',
        timeFrame,
        status,
        timeZoneId
      );
      return { timestamp, value, tooltipHtml };
    });
  }, [personVitalInsights, vitalType, timeFrame, xMinValue, xMaxValue]);

  // Notify parent about data availability
  React.useEffect(() => {
    onChartDataAvailabilityChange?.(chartDataObjects.length > 0);
  }, [chartDataObjects.length, onChartDataAvailabilityChange]);

  const HeartBreathChartTooltip: React.FC = () => {
    const tooltipData = useItemTooltip();
    if (!tooltipData || tooltipData.identifier.dataIndex == null) {
      return null;
    }
    const idx = tooltipData.identifier.dataIndex;
    const dp = chartDataObjects[idx];
    return <div dangerouslySetInnerHTML={{ __html: dp.tooltipHtml }} />;
  };

  // Render chart title visibly using Text, and remove default SVG margins on left/bottom
  return chartDataObjects.length > 0 ? (
    <div className={style.chartWrapper}>
      <LineChart
        className={style.chartContainer}
        title={title}
        margin={{ left: 0, bottom: 0 }}
        // Use dataset and stacked bars to represent range [lower, lower + diff]
        dataset={chartDataObjects}
        series={[{ id: 'range', type: 'line', dataKey: 'value', stack: 'range', color: chartColor }]}
        xAxis={[
          {
            id: 'xAxis',
            data: chartDataObjects.map((d) => d.timestamp),
            min: xMinValue,
            max: xMaxValue,
            scaleType: 'band',
            disableTicks: true,
            // show labels every 2 hours for 24h timeframe, and every other day for 30-day timeframe
            tickInterval:
              timeFrame === PersonVitalTimeFrame.TWELVE_MONTHS
                ? (_value, index) => true
                : timeFrame === PersonVitalTimeFrame.TWENTY_FOUR_HOURS
                  ? (_value, index) => index % 4 === 0
                  : timeFrame === PersonVitalTimeFrame.THIRTY_DAYS
                    ? (_value, index) => index % 2 === 0
                    : undefined,
            valueFormatter: (value, context) =>
              moment(value as Date)
                .tz(timeZoneId)
                .format(getDateFormat(timeFrame) ?? '')
          }
        ]}
        yAxis={[
          {
            id: 'yAxis',
            domainLimit: 'strict',
            tickMinStep: vitalType == PersonVitalType.BED_EXITS ? 1 : 0,
            tickMaxStep: vitalType == PersonVitalType.BED_EXITS ? 1 : 120,
            valueFormatter: (value: number) => formatValueString(value),
            fill: chartColor,
            tickLabelStyle: { fill: chartColor }
          }
        ]}
        height={CHART_HEIGHT}
        grid={{ horizontal: true }}
        slotProps={{
          tooltip: { trigger: 'none' }
        }}
      >
        <ChartsTooltipContainer trigger="item">
          <HeartBreathChartTooltip />
        </ChartsTooltipContainer>
        <ChartsReferenceLine y={nominalBounds[0]} lineStyle={{ stroke: chartColor, strokeDasharray: '4 4' }} label="Nominal Range Low" />
        <ChartsReferenceLine y={nominalBounds[1]} lineStyle={{ stroke: chartColor, strokeDasharray: '4 4' }} label="Nominal Range High" />
      </LineChart>
      {timeFrame === PersonVitalTimeFrame.TWENTY_FOUR_HOURS && !isToday && (
        <div className={style.dateAxis}>
          <div>{moment.tz(selectedDate, timeZoneId).format('MMM D')}</div>
          <div>{moment.tz(selectedDate, timeZoneId).add(1, 'day').format('MMM D')}</div>
        </div>
      )}
    </div>
  ) : (
    <div className={style.noDataChartContainer}>
      <CircledCustomIcon icon={TableIcon} />
      <Text>No data to display</Text>
    </div>
  );
};
